import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { EnumDropdownItemVariant, Typography, type IDropdownItem } from 'shared/ui'
import {
  type IParamsKnowledgeBaseDocuments,
  KnowledgeBaseApi,
  KnowledgeBaseDocument,
  KnowledgeBaseDocumentStatus,
} from 'entities/KnowledgeBase'
import styles from './styles.module.scss'
import { type AgentKnowledgeBaseStore } from './AgentKnowledgeBaseStore'

export class AgentDocumentsStore {
  private _documents: KnowledgeBaseDocument[] = []
  private _selectedDocumentIds: number[] = []
  private _isDocumentsLoading = true
  private _chatbotKnowledgeBaseStore: AgentKnowledgeBaseStore
  private _documentsError = ''

  constructor(
    chatbotKnowledgeBaseStore: AgentKnowledgeBaseStore,
    private readonly _isInitAllDocuments?: boolean
  ) {
    this._chatbotKnowledgeBaseStore = chatbotKnowledgeBaseStore
    makeAutoObservable(this)
  }

  get params() {
    const isSelectAllDocuments = this._initLoading
      ? this._isInitAllDocuments ?? this.isSelectAllDocuments
      : this.isSelectAllDocuments
    return {
      isSelectAllDocuments,
      selectedDocumentIds: this.selectedDocumentIds,
    }
  }

  get documents() {
    return this._documents
  }

  get selectedDocumentIds() {
    return this._selectedDocumentIds
  }

  get selectedDocuments() {
    return this.dropdownItems.filter((document) =>
      this._selectedDocumentIds.includes(Number(document.id))
    )
  }

  get isDocumentsLoading() {
    return this._isDocumentsLoading
  }

  get isSelectAllDocuments() {
    return this._selectedDocumentIds.length === this._documents.length
  }

  get documentsError() {
    return this._documentsError
  }

  get dropdownItems(): IDropdownItem[] {
    return [
      {
        id: 0,
        label: 'Select all documents',
        customFields: {
          leftIconColor: 'var(--content-primary-tertiary)',
          iconClassName: styles.iconCheckList,
        },
        variant: EnumDropdownItemVariant.Checkbox,
        activeValue: this.isSelectAllDocuments,
        labelWidth: '410px',
        divider: true,
      },
      ...this._documents.map((document) => ({
        id: document.id,
        label: document.name,
        customFields: {
          leftIconColor: 'var(--content-primary-tertiary)',
          iconClassName: styles.iconCheckList,
        },
        variant: EnumDropdownItemVariant.Checkbox,
        activeValue: this._selectedDocumentIds.includes(document.id),
        labelWidth: '410px',
        labelContent: (
          <div className={styles.documentDropdownLabel}>
            <Typography
              ellipsis
              variant={'body-md-regular'}
              tooltipProps={{
                label: document.name,
                placement: 'top',
              }}
              ariaLabel={'documentName'}
            >
              {document.name}
            </Typography>
          </div>
        ),
      })),
    ]
  }

  get isKnowledgeBaseDocumentsEmpty() {
    return (
      !this._isDocumentsLoading &&
      this._documents.length === 0 &&
      !this._chatbotKnowledgeBaseStore.knowledgeBase?.isInitStatus
    )
  }

  private _initLoading = true
  init = async (agentDocumentsIds: number[]) => {
    if (!this._initLoading) return
    await this.loadDocuments()
    this.initializeSelectedDocumentIds(agentDocumentsIds)
    runInAction(() => {
      this._initLoading = false
    })
  }

  initializeSelectedDocumentIds = (agentDocumentsIds: number[]) => {
    if (agentDocumentsIds.length > 0) {
      this.setSelectedDocumentIds(agentDocumentsIds)
    } else {
      if (this._isInitAllDocuments !== false) {
        this.selectAllDocuments()
      }
    }
  }

  loadDocuments = async () => {
    if (!this._chatbotKnowledgeBaseStore.knowledgeBase) return

    try {
      runInAction(() => {
        this._isDocumentsLoading = true
      })

      const params: IParamsKnowledgeBaseDocuments = {
        limit: 25,
        page: 1,
      }

      const { data } = await KnowledgeBaseApi.getKnowledgeBaseDocuments(params)

      runInAction(() => {
        this._documents = data.data
          .filter((doc) => doc.status === KnowledgeBaseDocumentStatus.IndexationSuccess)
          .map((doc) => new KnowledgeBaseDocument(doc))
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._isDocumentsLoading = false
      })
    }
  }

  setSelectedDocumentIds = (ids: number[]) => {
    this._selectedDocumentIds = ids
  }

  toggleDocumentSelection = (id: number) => {
    if (this._selectedDocumentIds.includes(id)) {
      this._selectedDocumentIds = this._selectedDocumentIds.filter((docId) => docId !== id)
    } else {
      this._selectedDocumentIds = [...this._selectedDocumentIds, id]
    }
  }

  selectAllDocuments = () => {
    this.setSelectedDocumentIds(this._documents.map((document) => document.id))
  }

  deselectAllDocuments = () => {
    this._selectedDocumentIds = []
  }

  handleSelectAllToggle = () => {
    if (this._selectedDocumentIds.length === this._documents.length && this._documents.length > 0) {
      this.deselectAllDocuments()
    } else {
      this.selectAllDocuments()
    }
  }

  handleSelectChange = (item: IDropdownItem) => {
    const id = Number(item.id)
    if (id === 0) {
      this.handleSelectAllToggle()
    } else {
      this.toggleDocumentSelection(id)
    }
    this.setDocumentsError('')
  }

  setDocumentsError = (error: string) => {
    this._documentsError = error
  }

  validateDocumentsList = (onError?: () => void) => {
    if (
      !this._initLoading &&
      this._documents.length > 0 &&
      this._selectedDocumentIds.length === 0
    ) {
      this.setDocumentsError('Please select at least one document')
    } else {
      this.setDocumentsError('')
    }
    onError?.()
  }
}
